import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class SegmentedControl implements Plugin {
  private readonly tabs: HTMLElement[];
  private readonly selectedClass: string;
  private readonly stateful: boolean;
  private readonly contentSelector: string;
  private readonly stateSelectedClass: string;
  private readonly stateDefaultClass: string;
  private readonly iconSelector: string;
  private readonly iconSelectedClass: string;
  private readonly iconDefaultClass: string;
  private readonly tabClickHandlers = new Map<
    HTMLElement,
    (e: Event) => void
  >();
  private readonly selectHandler: (e: Event) => void;

  constructor(private readonly element: HTMLElement) {
    const itemSelector =
      element.dataset.segmentedItemSelector || "[data-tab-id]";
    this.tabs = Array.from(element.querySelectorAll<HTMLElement>(itemSelector));
    this.selectedClass =
      element.dataset.segmentedSelectedClass || "core-tab--selected";
    this.stateful = element.dataset.segmentedStateful === "true";
    this.contentSelector =
      element.dataset.segmentedContentSelector || ".core-dropdown__content";
    this.stateSelectedClass =
      element.dataset.segmentedStateSelectedClass ||
      "core-dropdown__content--state-selected";
    this.stateDefaultClass =
      element.dataset.segmentedStateDefaultClass ||
      "core-dropdown__content--state-default";
    this.iconSelector =
      element.dataset.segmentedIconSelector || ".core-dropdown__content__radio";
    this.iconSelectedClass =
      element.dataset.segmentedIconSelectedClass ||
      "dsc-co-icon-radio-button-selected-circle-base-fill";
    this.iconDefaultClass =
      element.dataset.segmentedIconDefaultClass || "dsc-co-icon-radio-default";
    this.selectHandler = (e) => {
      const customEvent = e as CustomEvent<{ tabId?: string | number }>;
      const rawTabId = customEvent.detail?.tabId;
      if (rawTabId === undefined || rawTabId === null) return;

      const tabId = String(rawTabId);
      const targetTab = this.tabs.find((tab) => tab.dataset.tabId === tabId);
      if (targetTab) {
        this.handleTabClick(targetTab);
        return;
      }

      const index = Number(tabId);
      if (!Number.isNaN(index) && this.tabs[index]) {
        this.handleTabClick(this.tabs[index]);
      }
    };
  }

  init(): void {
    if (this.tabs.length === 0) return;

    this.tabs.forEach((tab) => {
      const clickHandler = (e: Event) => {
        e.preventDefault();
        this.handleTabClick(tab);
      };
      this.tabClickHandlers.set(tab, clickHandler);
      tab.addEventListener("click", clickHandler);
    });

    this.element.addEventListener(
      "segmentedControl:select",
      this.selectHandler,
    );

    if (this.stateful) {
      const selectedTab =
        this.tabs.find((tab) => tab.classList.contains(this.selectedClass)) ||
        this.tabs[0];
      this.tabs.forEach((tab) => {
        this.syncStateContent(tab, tab === selectedTab);
      });
    }
  }

  destroy(): void {
    this.tabs.forEach((tab) => {
      const clickHandler = this.tabClickHandlers.get(tab);
      if (clickHandler) {
        tab.removeEventListener("click", clickHandler);
      }
    });
    this.tabClickHandlers.clear();
    this.element.removeEventListener(
      "segmentedControl:select",
      this.selectHandler,
    );
  }

  private handleTabClick(clickedTab: HTMLElement): void {
    this.tabs.forEach((tab) => {
      tab.classList.remove(this.selectedClass);
      if (tab.getAttribute("aria-selected")) {
        tab.setAttribute("aria-selected", "false");
      }
      this.syncStateContent(tab, false);
    });

    clickedTab.classList.add(this.selectedClass);
    if (clickedTab.getAttribute("aria-selected")) {
      clickedTab.setAttribute("aria-selected", "true");
    }
    this.syncStateContent(clickedTab, true);

    const tabId =
      clickedTab.dataset.tabId || String(this.tabs.indexOf(clickedTab));
    this.element.dispatchEvent(
      new CustomEvent("segmentChanged", {
        detail: { tabId: tabId },
        bubbles: true,
      }),
    );
  }

  private syncStateContent(tab: HTMLElement, selected: boolean): void {
    if (!this.stateful) return;

    const content = tab.querySelector<HTMLElement>(this.contentSelector);
    if (content) {
      content.classList.remove(this.stateSelectedClass, this.stateDefaultClass);
      content.classList.add(
        selected ? this.stateSelectedClass : this.stateDefaultClass,
      );
    }

    const icon = tab.querySelector<HTMLElement>(this.iconSelector);
    if (icon) {
      icon.classList.remove(this.iconSelectedClass, this.iconDefaultClass);
      icon.classList.add(
        selected ? this.iconSelectedClass : this.iconDefaultClass,
      );
    }
  }
}

PluginRegistry.register("segmented-control", SegmentedControl);
